import { memo, useState, useCallback, forwardRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface PasswordInputProps {
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  className?: string;
  id?: string;
}

export const PasswordInput = memo(forwardRef<HTMLInputElement, PasswordInputProps>(({
  placeholder = "••••••••",
  disabled = false,
  hasError = false,
  value,
  onChange,
  onBlur,
  name,
  className,
  id,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  }, [onChange]);

  return (
    <div className="relative w-full">
      <Input
        ref={ref}
        id={id}
        type={showPassword ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        name={name}
        disabled={disabled}
        className={cn(
          "w-full pr-10",
          hasError && 'border-red-300 focus:border-red-500 focus:ring-red-500/20',
          className
        )}
        {...props}
      />
      <button
        type="button"
        onClick={togglePasswordVisibility}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        disabled={disabled}
        aria-label={showPassword ? "Hide password" : "Show password"}
      >
        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}));

PasswordInput.displayName = 'PasswordInput'; 
